/* eslint-disable react/jsx-props-no-spreading */
import React, {
  type CSSProperties,
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '@mui/material';

import { ReactComponent as DragIcon } from '../icons/drag_handler.svg';

import styles from './SortableItem.module.css';

interface Props {
  id: UniqueIdentifier;
  style: CSSProperties;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
}

interface Context {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableItem({
  children,
  id,
  style,
  onMouseEnter,
  onMouseLeave,
}: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );

  return (
    <SortableItemContext.Provider value={context}>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={styles.sortable_item}
        ref={setNodeRef}
        style={{
          opacity: isDragging ? 0.4 : undefined,
          transform: CSS.Translate.toString(transform),
          transition,
          ...style,
        }}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  );
}

interface DragProps {
  isDisabled: boolean;
}

export function DragHandle({ isDisabled }: DragProps) {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <IconButton
      className={styles.sortable_item_drag_handle}
      {...attributes}
      {...listeners}
      ref={ref}
      disabled={isDisabled}
    >
      <DragIcon />
    </IconButton>
  );
}
